import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PsaService } from '../../../../services/psa.service';
import { AzioneMitigatrice } from '../psa-configurator.model';

/**
 * @Author Tommaso Lazzaretti
 *
 * AttachActionComponent
 *
 * This Angular component is responsible for attaching mitigation actions to a specific event.
 * It allows for searching, filtering, and selecting actions from a list, as well as creating new actions.
 * Additionally, users can modify the rank (risk reduction) of each action and save their changes.
 *
 * Dependencies:
 * - MAT_DIALOG_DATA: Data passed to the component when it is opened as a dialog.
 * - MatDialogRef: Reference to the dialog, allowing it to be closed programmatically.
 * - PsaService: Service containing API calls for retrieving and managing mitigation actions.
 * - MatDialog: Service to handle opening and managing dialogs.
 */
@Component({
  selector: 'ebw-monorepo-attach-action',
  templateUrl: './attach-action.component.html',
  styleUrls: ['./attach-action.component.scss']
})
export class AttachActionComponent implements OnInit {
  @ViewChild('myInput') myInput: ElementRef<HTMLInputElement>;

  searchTerm: string = '';
  // associatedActions: any[] = [];
  listActions: any[] = [];
  listActionsFiltered: any[] = [];

  selectedActions: any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _dialogRef: MatDialogRef<AttachActionComponent>,
    private _psaService: PsaService
  ) {}

  ngOnInit(): void {
    // Se non ho data non faccio niente
    if (!this.data?.id) {
      return console.error('[AttachActionComponent][OnInit] data not found');
    }

    this._psaService._getEventActions(this.data.id).subscribe((actions) => {
      this.listActions = actions;
      // recupero la lista di azioni già associate e le seleziono di default
      this.selectedActions = [...this.data.associatedActions];
      this.selectedActions.forEach((action) => {
        action.old = true;
      });

      this._filterOptions();
    });
  }

  /**
   * Filters the list of available actions, excluding those already selected,
   * and applies any search term to narrow down the list.
   */
  _filterOptions() {
    const filterValue = this.searchTerm ? this.searchTerm.toLowerCase() : null;

    // Escludi gli elementi già selezionati
    this.listActionsFiltered = this.listActions.filter(
      (option) =>
        !this.selectedActions.some(
          (action: AzioneMitigatrice) => action.azione_mitigatrice_id === option.azione_mitigatrice_id
        )
    );
    //
    // {
    //   "id":string,
    //   "evento_rischio_id":string,
    //   "azione_mitigatrice_id":string,
    //   "tenant_id":string,
    //   "azione": {
    //     "id":string,
    //     "nome":string,
    //     "descrizione":string,
    //     "tenant_id":string
    //   }
    // }
    if (filterValue)
      this.listActionsFiltered = this.listActionsFiltered.filter((option) =>
        option.azione.nome.toLowerCase().includes(filterValue)
      ); // Filtra in base al valore dell'input
  }

  onInput(): void {
    this._filterOptions();
  }

  onRemoveAction(action) {
    // se l'azione non è nuova non posso farci nulla
    if (action.old) {
      return;
    }
    const temp = this.selectedActions.filter((a) => a.azione_mitigatrice_id !== action.azione_mitigatrice_id);
    this.selectedActions = [...temp];
    this._filterOptions();
  }

  /**
   * Handles the selection of an action from the list, adding it to the associated actions list
   * and clearing the search input field.
   */
  onOptionSelected(selectedValue): void {
    // Aggiungi l'elemento selezionato alla lista
    this.selectedActions.push(selectedValue);
    // Svuota il contenuto dell'input
    this.searchTerm = null;
    this.myInput.nativeElement.value = null;
    this._filterOptions();
  }

  /**
   * Checks if any action is currently selected from the actions list.
   *
   * @returns {boolean} True if at least one action is selected, otherwise false.
   */
  isAnyActionAdded(): boolean {
    return this.selectedActions.some((action) => !action['old']);
  }

  /**
   * Handles changes to the rank (risk reduction) of an associated action.
   *
   * @param {any} action - The action whose rank is being modified.
   * @param {number} rank - The new rank value.
   */
  onRankChange(action, rank, rankProp: 'riduzione_probabilita') {
    action[rankProp] = rank;
  }

  onClose() {
    this._dialogRef.close(null);
  }

  /**
   * Saves the associated actions by mapping the response to the format expected by the backend
   * and closes the dialog, returning the selected actions.
   */
  onSave() {
    this._dialogRef.close(this.mapRespForBE(this.selectedActions));
  }

  /**
   * Maps the response data to the format required by the backend, filtering only the selected actions
   * and including their ID, risk reduction, and status.
   *
   * @param {any[]} input - List of associated actions.
   * @returns {any[]} Mapped list of actions in the backend format.
   */
  mapRespForBE(selectedActions) {
    return selectedActions
      .filter((item) => !item.old) // Filtra solo le azioni aggiunte adesso
      .map((item) => ({
        azione_mitigatrice_id: item.azione_mitigatrice_id,
        riduzione_probabilita: item.riduzione_probabilita || 0,
        stato: item.stato ? 1 : 0
      }));
  }
}
